import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { authAPI } from '../api/modules/auth.js'
import { usersAPI } from '../api/modules/users.js'
import { useCartStore } from './cart.js'
import * as Sentry from '@sentry/vue'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(null)
  const isLoading = ref(false)
  const error = ref(null)

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isCustomer = computed(() => user.value?.role === 'customer')
  const isSalesman = computed(() => user.value?.role === 'salesman')
  const isB2B = computed(() => user.value?.account_type === 'company')
  const isB2C = computed(() => user.value?.account_type === 'individual')
  const hasVatId = computed(() => user.value?.ic_dph && user.value.ic_dph.trim() !== '')
  const shouldShowVatExclusivePrimary = computed(() => hasVatId.value)
  const displayName = computed(() => {
    if (!user.value) return ''
    if (user.value.account_type === 'company' && user.value.company_name) {
      return user.value.company_name
    }
    return user.value.full_name || `${user.value.first_name} ${user.value.last_name}`
  })

  // Actions
  const setAuth = (userData, tokenData, rememberMe = false) => {
    user.value = userData
    token.value = tokenData
    
    // Set Sentry user context
    if (userData?.email) {
      Sentry.setUser({ email: userData.email })
    }
    
    // Store in localStorage or sessionStorage based on rememberMe
    if (typeof window !== 'undefined') {
      if (rememberMe) {
        // Store in localStorage for persistent login
        localStorage.setItem('auth_token', tokenData)
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('remember_me', 'true')
        // Clear any session storage
        sessionStorage.removeItem('auth_token')
        sessionStorage.removeItem('user')
      } else {
        // Store in sessionStorage for session-only login
        sessionStorage.setItem('auth_token', tokenData)
        sessionStorage.setItem('user', JSON.stringify(userData))
        // Clear any localStorage auth data
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        localStorage.removeItem('remember_me')
      }
    }
  }

  const clearAuth = () => {
    user.value = null
    token.value = null
    error.value = null
    
    // Clear Sentry user context
    Sentry.setUser(null)
    
    // Clear both localStorage and sessionStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      localStorage.removeItem('remember_me')
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('user')
    }
  }

  const initAuth = () => {
    if (typeof window !== 'undefined') {
      // Check localStorage first (remember me)
      let storedToken = localStorage.getItem('auth_token')
      let storedUser = localStorage.getItem('user')
      let isRemembered = localStorage.getItem('remember_me') === 'true'
      
      // If not in localStorage, check sessionStorage (session only)
      if (!storedToken || !storedUser) {
        storedToken = sessionStorage.getItem('auth_token')
        storedUser = sessionStorage.getItem('user')
        isRemembered = false
      }
      
      if (storedToken && storedUser) {
        try {
          token.value = storedToken
          user.value = JSON.parse(storedUser)
          
          // Set Sentry user context on auth restoration
          if (user.value?.email) {
            Sentry.setUser({ email: user.value.email })
          }
          
          // Log the restoration for debugging
          console.log('Auth restored from', isRemembered ? 'localStorage (remembered)' : 'sessionStorage (session only)')
          
          // Return true to indicate successful restoration
          return true
        } catch (e) {
          console.error('Failed to parse stored user data:', e)
          clearAuth()
          return false
        }
      }
    }
    return false
  }

  const login = async (credentials) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Login attempt started', {
        email: credentials.email,
        remember_me: credentials.remember_me || false
      })
      
      // Capture guest cart ID before authentication
      const cartStore = useCartStore()
      const guestCartId = cartStore.cart?.id
      console.log('Captured guest cart ID before login:', guestCartId)

      const response = await authAPI.login(credentials)
      
      // Pass rememberMe flag to setAuth
      setAuth(response.user, response.token, credentials.remember_me || false)
      
      Sentry.logger.info('Login successful', {
        user_id: response.user?.id,
        user_role: response.user?.role,
        account_type: response.user?.account_type,
        has_guest_cart: !!guestCartId
      })
      
      // Transfer guest cart to authenticated user (reprice with B2B pricing)
      if (guestCartId && cartStore.hasItems) {
        try {
          console.log('Attempting to transfer cart:', guestCartId)
          const transferResult = await cartStore.transferCart(guestCartId)
          console.log('Cart transfer result:', transferResult)
          Sentry.logger.info('Cart transferred after login', {
            guest_cart_id: guestCartId,
            user_id: response.user?.id
          })
        } catch (e) {
          console.warn('Cart transfer after login failed:', e)
          Sentry.logger.warn('Cart transfer after login failed', {
            guest_cart_id: guestCartId,
            user_id: response.user?.id,
            error: e.message
          })
          // If transfer fails, refresh cart to get the authenticated user's cart
          try {
            await cartStore.getCart()
          } catch (refreshError) {
            console.warn('Failed to refresh cart after transfer failure:', refreshError)
          }
        }
      } else {
        // No guest cart with items to transfer, just refresh to get authenticated user's cart
        try {
          await cartStore.getCart()
        } catch (refreshError) {
          console.warn('Failed to refresh cart after login:', refreshError)
        }
      }
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Login failed'
      Sentry.logger.error('Login failed', {
        email: credentials.email,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Registration attempt started', {
        email: userData.email,
        account_type: userData.account_type
      })
      
      // Capture guest cart ID before registration
      const cartStore = useCartStore()
      const guestCartId = cartStore.cart?.id

      const response = await authAPI.register(userData)
      
      // For registration, default to not remembering (user can login with remember me later)
      setAuth(response.user, response.token, false)
      
      Sentry.logger.info('Registration successful', {
        user_id: response.user?.id,
        email: userData.email,
        account_type: userData.account_type,
        has_guest_cart: !!guestCartId
      })
      
      // Transfer guest cart to authenticated user (reprice with B2B pricing)
      if (guestCartId) {
        try {
          await cartStore.transferCart(guestCartId)
          Sentry.logger.info('Cart transferred after registration', {
            guest_cart_id: guestCartId,
            user_id: response.user?.id
          })
        } catch (e) {
          console.warn('Cart transfer after registration failed:', e)
          Sentry.logger.warn('Cart transfer after registration failed', {
            guest_cart_id: guestCartId,
            user_id: response.user?.id,
            error: e.message
          })
        }
      }
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Registration failed'
      Sentry.logger.error('Registration failed', {
        email: userData.email,
        account_type: userData.account_type,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      isLoading.value = true
      
      const userId = user.value?.id
      
      if (token.value) {
        await authAPI.logout()
      }
      
      Sentry.logger.info('Logout successful', {
        user_id: userId
      })
      
      clearAuth()
      
      // Refresh cart after logout to get guest cart
      const cartStore = useCartStore()
      try {
        await cartStore.getCart()
      } catch (e) {
        console.warn('Failed to refresh cart after logout:', e)
      }
      
      return { success: true }
    } catch (err) {
      // Clear auth even if logout request fails
      Sentry.logger.warn('Logout API call failed but auth cleared', {
        error: err.message,
        status_code: err.response?.status
      })
      clearAuth()
      
      // Still try to refresh cart
      const cartStore = useCartStore()
      try {
        await cartStore.getCart()
      } catch (e) {
        console.warn('Failed to refresh cart after logout:', e)
      }
      
      return { success: true }
    } finally {
      isLoading.value = false
    }
  }

  const refreshToken = async () => {
    try {
      Sentry.logger.debug('Token refresh attempt', {
        user_id: user.value?.id
      })
      
      const response = await authAPI.refreshToken()
      token.value = response.token
      
      if (typeof window !== 'undefined') {
        // Update token in the correct storage based on remember_me flag
        const isRemembered = localStorage.getItem('remember_me') === 'true'
        if (isRemembered) {
          localStorage.setItem('auth_token', response.token)
        } else {
          sessionStorage.setItem('auth_token', response.token)
        }
      }
      
      Sentry.logger.info('Token refresh successful', {
        user_id: user.value?.id
      })
      
      return { success: true, data: response }
    } catch (err) {
      Sentry.logger.error('Token refresh failed', {
        user_id: user.value?.id,
        error: err.response?.data?.message || 'Token refresh failed',
        status_code: err.response?.status
      })
      clearAuth()
      return { success: false, error: err.response?.data?.message || 'Token refresh failed' }
    }
  }

  // Replace the logged-in user (after a profile change), in memory and in the storage it was restored from
  const setUser = (userData) => {
    user.value = userData
    if (typeof window !== 'undefined') {
      const storage = localStorage.getItem('remember_me') === 'true' ? localStorage : sessionStorage
      storage.setItem('user', JSON.stringify(userData))
    }
  }

  // Reload the logged-in user from the API (the stored copy may predate new fields)
  const refreshUser = async () => {
    if (!token.value) return
    const response = await usersAPI.getProfile()
    if (response?.user) setUser(response.user)
  }

  const switchAccountType = async (accountType, companyData = {}) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Account type switch attempt', {
        user_id: user.value?.id,
        from_account_type: user.value?.account_type,
        to_account_type: accountType
      })
      
      const response = await authAPI.switchAccountType(accountType, companyData)
      
      // Update user data
      user.value = response.user
      if (typeof window !== 'undefined') {
        // Update user in the correct storage based on remember_me flag
        const isRemembered = localStorage.getItem('remember_me') === 'true'
        if (isRemembered) {
          localStorage.setItem('user', JSON.stringify(response.user))
        } else {
          sessionStorage.setItem('user', JSON.stringify(response.user))
        }
      }
      
      Sentry.logger.info('Account type switch successful', {
        user_id: response.user?.id,
        new_account_type: accountType
      })
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Account type switch failed'
      Sentry.logger.error('Account type switch failed', {
        user_id: user.value?.id,
        to_account_type: accountType,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const forgotPassword = async (email) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Forgot password request', {
        email: email
      })
      
      const response = await authAPI.forgotPassword(email)
      
      Sentry.logger.info('Forgot password email sent', {
        email: email
      })
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Password reset request failed'
      Sentry.logger.error('Forgot password request failed', {
        email: email,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (data) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Password reset attempt', {
        has_token: !!data.token
      })
      
      const response = await authAPI.resetPassword(data)
      
      Sentry.logger.info('Password reset successful')
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Password reset failed'
      Sentry.logger.error('Password reset failed', {
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (data) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Password change attempt', {
        user_id: user.value?.id
      })
      
      const response = await authAPI.changePassword(data)
      
      Sentry.logger.info('Password change successful', {
        user_id: user.value?.id
      })
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Password change failed'
      Sentry.logger.error('Password change failed', {
        user_id: user.value?.id,
        error: error.value,
        status_code: err.response?.status
      })
      throw err // Re-throw to handle in component
    } finally {
      isLoading.value = false
    }
  }

  const verifyEmail = async (token) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Email verification attempt')
      
      const response = await authAPI.verifyEmail(token)
      
      Sentry.logger.info('Email verification successful', {
        user_id: response.user?.id
      })
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Email verification failed'
      Sentry.logger.error('Email verification failed', {
        error: error.value,
        status_code: err.response?.status
      })
      throw err // Re-throw to handle in component
    } finally {
      isLoading.value = false
    }
  }

  const resendVerificationEmail = async (email) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Resend verification email request', {
        email: email
      })
      
      const response = await authAPI.resendVerificationEmail(email)
      
      Sentry.logger.info('Verification email resent', {
        email: email
      })
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Resend verification failed'
      Sentry.logger.error('Resend verification email failed', {
        email: email,
        error: error.value,
        status_code: err.response?.status
      })
      throw err // Re-throw to handle in component
    } finally {
      isLoading.value = false
    }
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  const setLoading = (loading) => {
    isLoading.value = loading
  }

  return {
    // State
    user: readonly(user),
    token: readonly(token),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Computed
    isAuthenticated,
    isAdmin,
    isCustomer,
    isSalesman,
    isB2B,
    isB2C,
    hasVatId,
    shouldShowVatExclusivePrimary,
    displayName,
    
    // Actions
    setAuth,
    clearAuth,
    initAuth,
    login,
    register,
    logout,
    refreshToken,
    setUser,
    refreshUser,
    switchAccountType,
    forgotPassword,
    resetPassword,
    changePassword,
    verifyEmail,
    resendVerificationEmail,
    setError,
    clearError,
    setLoading
  }
})
